<template>
  <div class="page-container">
    
    <nav class="custom-nav" :class="{ 'scrolled': isScrolled }">
      <div class="nav-content">
        <div class="brand">
          <img src="/images/or-tech-white.png" alt="OR-TECH" class="nav-logo" />
          <div class="brand-text">
            <span class="brand-name">OR-TECH</span>
            <span class="brand-tag">Electrical Services</span>
          </div>
        </div>

        <div class="links desktop-links">
          <a v-for="item in navLinks" :key="item.id" @click="scrollTo(item.id)" :class="{ active: activeSection === item.id }">
            {{ t.nav[item.key] }}
          </a>
          <button @click="toggleLang" class="lang-switcher">
            <i class="pi pi-globe"></i>
            {{ currentLang === 'sq' ? 'EN' : 'SQ' }}
          </button>
        </div>

        <div class="mobile-toggle" @click="mobileMenuOpen = !mobileMenuOpen">
          <i class="pi" :class="mobileMenuOpen ? 'pi-times' : 'pi-bars'"></i>
        </div>
      </div>

      <transition name="fade">
        <div v-if="mobileMenuOpen" class="mobile-dropdown">
          <a v-for="item in navLinks" :key="item.id" @click="mobileScrollTo(item.id)">
            {{ t.nav[item.key] }}
          </a>
          <div class="mobile-lang" @click="toggleLang">
            Change Language: <strong>{{ currentLang === 'sq' ? 'English' : 'Shqip' }}</strong>
          </div>
        </div>
      </transition>
    </nav>

    <section id="home" class="hero-section">
      <div class="hero-bg" :style="{ backgroundImage: 'url(/images/hero-bg.jpg)' }"></div>
      <div class="hero-overlay"></div>
      
      <div class="hero-content">
        <img src="/images/or-tech-white.png" alt="OR-TECH" class="hero-logo" />
        <h1 class="hero-title">
          {{ t.hero.titleLine1 }} <span class="highlight">{{ t.hero.titleHighlight }}</span>
        </h1>
        <p class="hero-subtitle">{{ t.hero.subtitle }}</p>
        
        <div class="hero-actions">
          <a class="btn btn-primary" href="tel:+355692089211">
            <i class="pi pi-phone"></i> +355 69 208 9211
          </a>
          <button class="btn btn-outline" @click="scrollTo('projects')">
            {{ t.gallery.title }} <i class="pi pi-arrow-right"></i>
          </button>
        </div>
      </div>

      <div class="scroll-indicator" @click="scrollTo('services')">
        <div class="bolt-anim">
          <i class="pi pi-bolt"></i>
        </div>
        <span class="scroll-text">Scroll</span>
      </div>
    </section>

    <section id="services" class="section services-section">
      <div class="section-container">
        <div class="section-header center">
          <h2 class="section-title">{{ t.services.title }}</h2>
          <div class="title-bar"></div>
          <p class="section-desc">{{ t.services.subtitle }}</p>
        </div>

        <div class="services-grid">
          <div v-for="(s, index) in t.services.list" :key="index" class="service-card">
            <div class="icon-wrapper">
              <i :class="s.icon"></i>
            </div>
            <h3>{{ s.name }}</h3>
            <p>{{ s.desc }}</p>
          </div>
        </div>
      </div>
    </section>

    <section id="why-us" class="section why-us-section">
      <div class="section-container">
        <div class="why-us-grid">
          <div class="why-text">
            <h2 class="section-title text-left">{{ t.whyUs.title }}</h2>
            <div class="title-bar left"></div>
            <div class="features-list">
              <div v-for="feature in t.whyUs.features" :key="feature.title" class="feature-row">
                <div class="f-icon"><i :class="feature.icon"></i></div>
                <div class="f-content">
                  <h4>{{ feature.title }}</h4>
                  <p>{{ feature.desc }}</p>
                </div>
              </div>
            </div>
          </div>
          
          <div class="stats-wrapper">
            <div v-for="stat in t.whyUs.stats" :key="stat.label" class="stat-box">
              <span class="stat-number">{{ stat.value }}</span>
              <span class="stat-label">{{ stat.label }}</span>
            </div>
          </div>
        </div>
      </div>
    </section>

    <section id="projects" class="section projects-section">
      <div class="section-container">
        <div class="section-header center">
          <h2 class="section-title">{{ t.gallery.title }}</h2>
          <div class="title-bar"></div>
        </div>
      </div>
      
      <div
        class="carousel-wrapper"
        @pointerdown="onCarouselPointerDown"
        @pointerup="onCarouselPointerUp"
      >
        <p-carousel
          v-model:page="carouselPage"
          :value="projects"
          :numVisible="3"
          :numScroll="1"
          :responsiveOptions="responsiveOptions"
          :circular="true"
          :autoplayInterval="4000"
        >
          <template #item="slotProps">
            <div class="project-item">
              <div class="project-img-box">
                <img :src="slotProps.data.image" class="project-img" draggable="false" />
              </div>
            </div>
          </template>
        </p-carousel>
      </div>
    </section>

    <section class="section faq-section">
      <div class="section-container narrow">
        <div class="section-header center">
          <h2 class="section-title">{{ t.faq.title }}</h2>
          <div class="title-bar"></div>
        </div>

        <div class="faq-list">
          <div v-for="(item, i) in t.faq.questions" :key="i" class="faq-item" :class="{ open: openFaq === i }" @click="toggleFaq(i)">
            <div class="faq-question">
              <span>{{ item.q }}</span>
              <i class="pi" :class="openFaq === i ? 'pi-minus' : 'pi-plus'"></i>
            </div>
            <div class="faq-answer" :style="{ maxHeight: openFaq === i ? '200px' : '0' }">
              <p>{{ item.a }}</p>
            </div>
          </div>
        </div>
      </div>
    </section>

    <section id="contact" class="section contact-section">
      <div class="section-container">
        <div class="contact-box">
          <div class="contact-header">
            <h2>{{ t.contact.title }}</h2>
            <div class="title-bar"></div>
            <p>{{ t.contact.subtitle }}</p>
          </div>

          <div class="contact-cards">
            <a href="tel:+355692089211" class="c-card">
              <div class="c-icon"><i class="pi pi-phone"></i></div>
              <div class="c-info">
                <small>{{ t.contact.phone }}</small>
                <strong>+355 69 208 9211</strong>
              </div>
            </a>

            <a href="mailto:info@or-tech.al" class="c-card">
              <div class="c-icon"><i class="pi pi-envelope"></i></div>
              <div class="c-info">
                <small>Email</small>
                <strong>info@or-tech.al</strong>
              </div>
            </a>

            <div class="c-card">
              <div class="c-icon"><i class="pi pi-map-marker"></i></div>
              <div class="c-info">
                <small>{{ t.contact.location }}</small>
                <strong>{{ t.contact.address }}</strong>
              </div>
            </div>

            <!-- <a href="https://www.instagram.com/or_tech.al/" target="_blank" class="c-card instagram">
              <div class="c-icon"><i class="pi pi-instagram"></i></div>
              <div class="c-info">
                <small>Social Media</small>
                <strong>@or_tech.al</strong>
              </div>
            </a> -->
          </div>
        </div>
      </div>
    </section>

    <footer class="site-footer">
      <div class="footer-content">
        <div class="footer-brand">
          <img class="footer-logo" src="/images/or-tech-white.png" alt="OR-TECH" />
          <span>OR-TECH</span>
        </div>
        <div class="copyright">
          &copy; {{ currentYear }} OR-TECH Electrical Services. {{ t.footer.text }}
        </div>
      </div>
    </footer>

  </div>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted } from 'vue';

// --- State ---
const currentLang = ref('sq');
const currentYear = computed(() => new Date().getFullYear());
const openFaq = ref(0);
const isScrolled = ref(false);
const mobileMenuOpen = ref(false);
const activeSection = ref('home');
const carouselPage = ref(0);
const startX = ref(0);

// --- Data ---
const projects = ref([
  { image: '/images/2.jpeg' },
  { image: '/images/3.jpeg' },
  { image: '/images/4.jpeg' },
  { image: '/images/5.jpeg' },
  { image: '/images/6.jpeg' },
  { image: '/images/7.jpeg' },
  { image: '/images/1.png' },
  { image: '/images/2.png' },
  { image: '/images/3.png' },
  { image: '/images/4.png' },
  { image: '/images/5.png' },
  // { image: '/images/6.png' },
  { image: '/images/7.png' },
  { image: '/images/8.png' },
  { image: '/images/9.png' },
  { image: '/images/10.png' },
  { image: '/images/11.png' },
  // { image: '/images/12.png' },
  { image: '/images/13.png' },
  { image: '/images/14.png' },
  { image: '/images/15.png' },
]);

const responsiveOptions = ref([
  { breakpoint: '1199px', numVisible: 2, numScroll: 1 },
  { breakpoint: '767px', numVisible: 1, numScroll: 1 }
]);

const navLinks = [
  { id: 'home', key: 'home' },
  { id: 'services', key: 'services' },
  { id: 'projects', key: 'projects' },
  { id: 'contact', key: 'contact' }
];

// --- Translations ---
const translations = {
  sq: {
    nav: { home: 'Kreu', projects: 'Projektet', services: 'Shërbimet', contact: 'Kontakt' },
    hero: {
      titleLine1: 'Zgjidhje Elektrike',
      titleHighlight: 'Profesionale',
      subtitle: 'Instalime Civile & Industriale • Mirëmbajtje • Projektim\nPartneri juaj i besuar për çdo nevojë energjetike.'
    },
    gallery: { title: 'Punimet Tona' },
    services: {
      title: 'Çfarë Ofrojmë',
      subtitle: 'Një gamë e plotë shërbimesh elektrike të certifikuara',
      list: [
        { name: 'Projektim', desc: 'Plane inxhinierike të detajuara sipas standardeve BE.', icon: 'pi pi-pencil' },
        { name: 'Instalime', desc: 'Zbatim për godina civile dhe industriale me saktësi maksimale.', icon: 'pi pi-bolt' },
        { name: 'Konsulencë', desc: 'Audit teknik dhe optimizim i kostove të energjisë.', icon: 'pi pi-comments' },
        { name: 'Riparime', desc: 'Ndërhyrje emergjente 24/7 për çdo defekt.', icon: 'pi pi-cog' },
        { name: 'Mirëmbajtje', desc: 'Kontrolle periodike për siguri dhe jetëgjatësi.', icon: 'pi pi-wrench' },
        { name: 'Smart Home', desc: 'Automatizim modern për komoditet dhe kursim.', icon: 'pi pi-home' }
      ]
    },
    whyUs: {
      title: 'Pse OR-TECH?',
      stats: [
        { value: '10+', label: 'Vite Eksperiencë' },
        { value: '50+', label: 'Projekte' },
        { value: '24/7', label: 'Suport' },
        { value: '100%', label: 'Garanci' }
      ],
      features: [
        { title: 'Standard i Lartë', desc: 'Përdorim materiale të certifikuara dhe teknologjinë më të fundit.', icon: 'pi pi-shield' },
        { title: 'Çmime Transparente', desc: 'Vlerësim i drejtë i kostos pa tarifa të fshehura.', icon: 'pi pi-tag' },
        { title: 'Staf i Kualifikuar', desc: 'Teknikë të licensuar me eksperiencë shumëvjeçare.', icon: 'pi pi-users' }
      ]
    },
    faq: {
      title: 'Pyetje të Shpeshta',
      questions: [
        { q: 'A ofroni mbështetje 24/7?', a: 'Po, ekipi ynë i emergjencës është i disponueshëm në çdo kohë për ndërhyrje urgjente.' },
        { q: 'Sa kushton një konsultë fillestare?', a: 'Konsulta fillestare dhe vlerësimi i punimeve është falas. Na kontaktoni për të planifikuar një vizitë.' },
        { q: 'A punoni me biznese dhe individë?', a: 'Po, ne ofrojmë shërbime si për klientë privatë ashtu edhe për biznese, duke përfshirë instalime industriale.' },
        { q: 'A janë instalimet me garanci?', a: 'Absolutisht. Çdo punim shoqërohet me garanci teknike dhe mbështetje të vazhdueshme pas përfundimit të projektit.' },
        { q: 'Sa zgjat zakonisht një projekt?', a: 'Kohëzgjatja varet nga kompleksiteti. Një instalim standard shtëpie përfundon brenda 3-7 ditëve pune.' }
      ]
    },
    contact: {
      title: 'Filloni Projektin Tuaj',
      subtitle: 'Na kontaktoni për një vlerësim falas.',
      phone: 'Telefon',
      location: 'Adresa',
      address: 'Shkodër, Shqipëri'
    },
    footer: { text: 'All Rights Reserved.' }
  },
  en: {
    nav: { home: 'Home', projects: 'Projects', services: 'Services', contact: 'Contact' },
    hero: {
      titleLine1: 'Professional',
      titleHighlight: 'Electrical Solutions',
      subtitle: 'Civil & Industrial Installations • Maintenance • Design\nYour trusted partner for every energy need.'
    },
    gallery: { title: 'Our Portfolio' },
    services: {
      title: 'Our Services',
      subtitle: 'A full range of certified electrical services',
      list: [
        { name: 'Design', desc: 'Detailed engineering plans according to EU standards.', icon: 'pi pi-pencil' },
        { name: 'Installations', desc: 'Execution for civil and industrial buildings with precision.', icon: 'pi pi-bolt' },
        { name: 'Consulting', desc: 'Technical audit and energy cost optimization.', icon: 'pi pi-comments' },
        { name: 'Repairs', desc: '24/7 emergency interventions for any defect.', icon: 'pi pi-cog' },
        { name: 'Maintenance', desc: 'Periodic checks for safety and longevity.', icon: 'pi pi-wrench' },
        { name: 'Smart Home', desc: 'Modern automation for comfort and savings.', icon: 'pi pi-home' }
      ]
    },
    whyUs: {
      title: 'Why OR-TECH?',
      stats: [
        { value: '10+', label: 'Years Exp.' },
        { value: '50+', label: 'Projects' },
        { value: '24/7', label: 'Support' },
        { value: '100%', label: 'Warranty' }
      ],
      features: [
        { title: 'High Standard', desc: 'We use certified materials and the latest technology.', icon: 'pi pi-shield' },
        { title: 'Transparent Pricing', desc: 'Fair cost assessment with no hidden fees.', icon: 'pi pi-tag' },
        { title: 'Qualified Staff', desc: 'Licensed technicians with years of experience.', icon: 'pi pi-users' }
      ]
    },
    faq: {
      title: 'Frequently Asked Questions',
      questions: [
        { q: 'Do you offer 24/7 support?', a: 'Yes, our emergency team is available around the clock for urgent interventions.' },
        { q: 'How much does an initial consultation cost?', a: 'The initial consultation and work assessment is free. Contact us to schedule a visit.' },
        { q: 'Do you work with businesses and individuals?', a: 'Yes, we offer services for both private clients and businesses, including industrial installations.' },
        { q: 'Are installations guaranteed?', a: 'Absolutely. Every project comes with a technical warranty and ongoing support after completion.' },
        { q: 'How long does a project typically take?', a: 'Duration depends on complexity. A standard home installation is completed within 3-7 working days.' }
      ]
    },
    contact: {
      title: 'Start Your Project',
      subtitle: 'Contact us for a free assessment.',
      phone: 'Phone',
      location: 'Location',
      address: 'Shkodra, Albania'
    },
    footer: { text: 'All Rights Reserved.' }
  }
};

const t = computed(() => translations[currentLang.value]);

// --- Methods ---
const toggleLang = () => { currentLang.value = currentLang.value === 'sq' ? 'en' : 'sq'; mobileMenuOpen.value = false; };
const toggleFaq = (i) => { openFaq.value = openFaq.value === i ? -1 : i; };

const scrollTo = (id) => {
  const el = document.getElementById(id);
  if (el) {
    const offset = 80;
    const bodyRect = document.body.getBoundingClientRect().top;
    const elementRect = el.getBoundingClientRect().top;
    const elementPosition = elementRect - bodyRect;
    const offsetPosition = elementPosition - offset;

    window.scrollTo({
      top: offsetPosition,
      behavior: 'smooth'
    });
  }
  mobileMenuOpen.value = false;
};

const mobileScrollTo = (id) => {
  scrollTo(id);
};

// Scroll Handler
const handleScroll = () => {
  isScrolled.value = window.scrollY > 50;
  
  // Active Link Update
  const sections = navLinks.map(l => l.id);
  for (const section of sections) {
    const el = document.getElementById(section);
    if (el) {
      const rect = el.getBoundingClientRect();
      if (rect.top <= 150 && rect.bottom >= 150) {
        activeSection.value = section;
      }
    }
  }
};

// --- Carousel Drag ---
const onCarouselPointerDown = (e) => {
  if (e.target.closest('.p-carousel-prev, .p-carousel-next, .p-carousel-indicator')) return;
  startX.value = e.clientX;
};

const onCarouselPointerUp = (e) => {
  if (startX.value === 0) return;

  const diff = e.clientX - startX.value;
  const threshold = 60;

  if (Math.abs(diff) > threshold) {
    const numVisible = window.innerWidth < 767 ? 1 : (window.innerWidth < 1199 ? 2 : 3);
    const totalPages = projects.value.length - numVisible + 1;

    if (diff > 0) {
      carouselPage.value = (carouselPage.value - 1 + totalPages) % totalPages;
    } else {
      carouselPage.value = (carouselPage.value + 1) % totalPages;
    }
  }
  startX.value = 0;
};

onMounted(() => {
  window.addEventListener('scroll', handleScroll);
});

onUnmounted(() => {
  window.removeEventListener('scroll', handleScroll);
});
</script>

<style scoped>
/* COLOR PALETTE (Based on Logo):
  Background: #020b16 (Deep Navy/Black)
  Primary: #00A8E8 (Cyan/Electric Blue)
  Accent: #FFD600 (Electric Yellow)
  Text: #E2E8F0 (Light Grey)
*/

@import url('https://fonts.googleapis.com/css2?family=Manrope:wght@400;500;700;800&display=swap');

/* --- GLOBAL RESET & BASE --- */
.page-container {
  font-family: 'Manrope', sans-serif;
  color: #e2e8f0;
  background-color: #020b16;
  overflow-x: hidden;
  line-height: 1.6;
}

h1, h2, h3, h4 { margin: 0; font-weight: 800; color: #fff; }
a { text-decoration: none; color: inherit; transition: 0.3s; }
ul { list-style: none; padding: 0; }
button { font-family: inherit; border: none; cursor: pointer; }

/* UTILS */
.section { padding: 100px 0; position: relative; }
.section-container { max-width: 1200px; margin: 0 auto; padding: 0 24px; position: relative; z-index: 2; }
.section-container.narrow { max-width: 800px; }
.text-left { text-align: left; }
.center { text-align: center; display: flex; flex-direction: column; align-items: center; }

.section-title { font-size: 2.5rem; letter-spacing: -1px; margin-bottom: 1rem; }
.title-bar { width: 60px; height: 4px; background: #FFD600; border-radius: 2px; margin-bottom: 24px; }
.title-bar.left { margin-left: 0; }

.section-desc { font-size: 1.1rem; color: #94a3b8; max-width: 600px; text-align: center; }

/* --- NAVIGATION --- */
.custom-nav {
  position: fixed;
  top: 0; left: 0; right: 0;
  height: 80px;
  z-index: 1000;
  transition: all 0.4s ease;
  background: transparent;
  padding: 0 24px;
}
.custom-nav.scrolled {
  background: rgba(2, 11, 22, 0.9);
  backdrop-filter: blur(20px);
  box-shadow: 0 4px 30px rgba(0, 0, 0, 0.3);
  height: 70px;
}

.nav-content {
  max-width: 1200px;
  margin: 0 auto;
  height: 100%;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.brand { display: flex; align-items: center; gap: 12px; }
.nav-logo { height: 55px; width: auto; }
.brand-text { display: flex; flex-direction: column; line-height: 1.1; margin-left: 0.4rem }
.brand-name { font-weight: 800; font-size: 1.2rem; color: #fff; letter-spacing: 0.5px; }
.brand-tag { font-size: 0.75rem; color: #00A8E8; font-weight: 600; text-transform: uppercase; letter-spacing: 1px; }

.desktop-links { display: flex; gap: 32px; align-items: center; }
.desktop-links a {
  font-weight: 600; font-size: 0.95rem; color: #94a3b8; position: relative;
  cursor: pointer;
}
.desktop-links a:hover, .desktop-links a.active { color: #fff; }
.desktop-links a::after {
  content: ''; position: absolute; bottom: -4px; left: 0; width: 0; height: 2px;
  background: #FFD600; transition: width 0.3s;
}
.desktop-links a:hover::after, .desktop-links a.active::after { width: 100%; }

.lang-switcher {
  display: flex; align-items: center; gap: 6px;
  background: rgba(255, 255, 255, 0.05);
  padding: 8px 16px; border-radius: 50px;
  color: #fff; font-weight: 700; font-size: 0.85rem;
  transition: 0.3s;
}
.lang-switcher:hover { background: #FFD600; color: #020b16; }

.mobile-toggle { display: none; font-size: 1.5rem; color: #fff; cursor: pointer; }

/* Mobile Dropdown */
.mobile-dropdown {
  position: absolute; top: 100%; left: 0; right: 0;
  background: #061325; border-top: 1px solid rgba(255,255,255,0.1);
  padding: 20px; display: flex; flex-direction: column; gap: 15px;
  box-shadow: 0 20px 40px rgba(0,0,0,0.5);
}
.mobile-dropdown a { padding: 10px; font-weight: 600; border-bottom: 1px solid rgba(255,255,255,0.05); }
.mobile-lang { padding: 10px; color: #FFD600; cursor: pointer; }

/* --- HERO SECTION --- */
.hero-section {
  height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  text-align: center;
  position: relative;
  overflow: hidden;
  padding-top: 60px;
}
.hero-bg {
  position: absolute; inset: 0;
  background-size: cover; background-position: center;
  z-index: 0; transform: scale(1.1);
  filter: blur(0px);
}
.hero-overlay {
  position: absolute; inset: 0; z-index: 1;
  background: linear-gradient(to bottom, rgba(2,11,22,0.5), #020b16);
}

.hero-content {
  position: relative; z-index: 2; max-width: 800px; padding: 0 24px;
  display: flex; flex-direction: column; align-items: center;
}

.hero-logo {
  height: 12rem; width: auto; margin-bottom: 30px;
  filter: drop-shadow(0 0 30px rgba(0, 168, 232, 0.3));
}

.hero-title {
  font-size: 3.5rem; line-height: 1.1; margin-bottom: 24px;
}
.highlight { color: #FFD600; text-shadow: 0 0 20px rgba(255, 214, 0, 0.3); }

.hero-subtitle {
  font-size: 1.15rem; color: #cbd5e1; margin-bottom: 40px; white-space: pre-line;
}

.hero-actions { display: flex; gap: 16px; flex-wrap: wrap; justify-content: center; }

.btn {
  padding: 14px 32px; border-radius: 50px; font-weight: 700; font-size: 1rem;
  display: inline-flex; align-items: center; gap: 10px; transition: all 0.3s;
}
.btn-primary {
  background: #FFD600; color: #020b16; box-shadow: 0 4px 15px rgba(255, 214, 0, 0.3);
}
.btn-primary:hover { transform: translateY(-2px); box-shadow: 0 8px 25px rgba(255, 214, 0, 0.4); background: #fff; }

.btn-outline {
  background: transparent; border: 2px solid rgba(255,255,255,0.2); color: #fff;
}
.btn-outline:hover { border-color: #fff; background: rgba(255,255,255,0.05); }

/* Scroll Indicator */
.scroll-indicator {
  position: absolute; bottom: 40px; left: 50%; transform: translateX(-50%);
  z-index: 2; cursor: pointer; display: flex; flex-direction: column;
  align-items: center; gap: 8px;
}
.bolt-anim {
  width: 44px; height: 44px; border-radius: 50%;
  border: 2px solid rgba(255, 214, 0, 0.4);
  display: flex; align-items: center; justify-content: center;
  animation: bolt-pulse 2s ease-in-out infinite;
}
.bolt-anim i {
  font-size: 1.2rem; color: #FFD600;
  animation: bolt-bounce 2s ease-in-out infinite;
}
.scroll-text {
  font-size: 0.7rem; color: rgba(255,255,255,0.5); text-transform: uppercase;
  letter-spacing: 2px; font-weight: 600;
}
@keyframes bolt-pulse {
  0%, 100% { border-color: rgba(255, 214, 0, 0.2); box-shadow: 0 0 0 0 rgba(255, 214, 0, 0); }
  50% { border-color: rgba(255, 214, 0, 0.6); box-shadow: 0 0 20px rgba(255, 214, 0, 0.15); }
}
@keyframes bolt-bounce {
  0%, 100% { transform: translateY(0); }
  50% { transform: translateY(4px); }
}

/* --- SERVICES --- */
.services-section { background: #020b16; }

.services-grid {
  display: grid; grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 30px; padding-top: 20px;
}
.service-card {
  background: #061325; padding: 40px 30px; border-radius: 20px;
  text-align: center; position: relative; overflow: hidden;
  border: 1px solid rgba(255,255,255,0.03); transition: 0.4s;
}
.service-card:hover {
  transform: translateY(-10px);
  border-color: rgba(255, 214, 0, 0.3);
  background: linear-gradient(160deg, #0d1f35, #081830);
}
.service-card:hover .icon-wrapper {
  background: rgba(255, 214, 0, 0.15);
}
.service-card:hover .icon-wrapper i { color: #FFD600; }
.service-card:hover h3 { color: #FFD600; }

.icon-wrapper {
  width: 60px; height: 60px; border-radius: 14px; background: rgba(0, 168, 232, 0.1);
  display: flex; align-items: center; justify-content: center;
  margin: 0 auto 24px auto; transition: 0.4s;
}
.icon-wrapper i { font-size: 1.5rem; color: #00A8E8; transition: 0.4s; }

.service-card h3 { font-size: 1.25rem; margin-bottom: 12px; transition: 0.4s; }
.service-card p { font-size: 0.95rem; color: #94a3b8; line-height: 1.6; margin: 0; }

/* --- WHY US --- */
.why-us-section { background: linear-gradient(180deg, #020b16 0%, #081a33 100%); }

.why-us-grid { display: grid; grid-template-columns: 1fr 0.8fr; gap: 60px; align-items: center; }

.feature-row { display: flex; gap: 20px; margin-bottom: 30px; }
.f-icon {
  width: 50px; height: 50px; flex-shrink: 0; border-radius: 50%;
  background: #FFD600; display: flex; align-items: center; justify-content: center;
  color: #020b16; font-size: 1.2rem;
}
.f-content h4 { font-size: 1.1rem; margin-bottom: 6px; }
.f-content p { color: #94a3b8; font-size: 0.9rem; margin: 0; }

.stats-wrapper {
  display: grid; grid-template-columns: repeat(2, 1fr); gap: 20px;
}
.stat-box {
  background: rgba(255,255,255,0.03); padding: 30px; border-radius: 16px;
  text-align: center; border: 1px solid rgba(255,255,255,0.05);
}
.stat-number { display: block; font-size: 2.5rem; font-weight: 800; color: #FFD600; line-height: 1; margin-bottom: 8px; }
.stat-label { font-size: 0.9rem; color: #fff; font-weight: 600; }

/* --- PROJECTS --- */
.projects-section { background: #020b16; overflow: hidden; }

.carousel-wrapper { cursor: grab; user-select: none; touch-action: pan-y; }
.carousel-wrapper:active { cursor: grabbing; }
.carousel-wrapper .project-img { pointer-events: none; }

.project-item { padding: 10px; }
.project-img-box {
  border-radius: 16px; overflow: hidden; position: relative; height: 450px;
  box-shadow: 0 10px 30px rgba(0,0,0,0.3);
}
.project-img { width: 100%; height: 100%; object-fit: cover; transition: 0.5s; }
.project-img-box:hover .project-img { transform: scale(1.05); }

:deep(.p-carousel-indicator-list) { gap: 8px; padding: 16px 0 0 0; }
:deep(.p-carousel-indicator-button) {
  width: 10px; height: 10px; border-radius: 50%;
  background: rgba(255, 255, 255, 0.2); border: none; transition: 0.3s;
}
:deep(.p-carousel-indicator-active .p-carousel-indicator-button) {
  background: #FFD600; width: 28px; border-radius: 10px;
}

/* --- FAQ --- */
.faq-item {
  background: #061325; border-radius: 12px; margin-bottom: 12px;
  border: 1px solid rgba(255,255,255,0.03); overflow: hidden;
  transition: all 0.3s;
}
.faq-item.open { border-color: #FFD600; }
.faq-question {
  padding: 20px; display: flex; justify-content: space-between; align-items: center;
  cursor: pointer; font-weight: 700; color: #fff;
}
.faq-question i { color: #FFD600; font-size: 0.8rem; }
.faq-answer {
  padding: 0 20px; overflow: hidden; transition: max-height 0.3s ease;
  color: #94a3b8; font-size: 0.95rem;
}
.faq-item.open .faq-answer { padding-bottom: 20px; }

/* --- CONTACT --- */
.contact-section { padding-bottom: 50px; background: linear-gradient(0deg, #020b16 0%, #081a33 100%); }

.contact-box {
  background: linear-gradient(135deg, #0a1e3d 0%, #0d2a4a 40%, #102e50 70%, #0a1e3d 100%);
  border-radius: 30px; padding: 60px; position: relative; overflow: hidden;
  border: 1px solid rgba(0, 168, 232, 0.15);
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.4);
}
.contact-box::before {
  content: ''; position: absolute; top: -50%; right: -20%; width: 400px; height: 400px;
  background: radial-gradient(circle, rgba(0, 168, 232, 0.08) 0%, transparent 70%);
  pointer-events: none;
}
.contact-box::after {
  content: ''; position: absolute; bottom: -30%; left: -10%; width: 300px; height: 300px;
  background: radial-gradient(circle, rgba(255, 214, 0, 0.06) 0%, transparent 70%);
  pointer-events: none;
}
.contact-header { text-align: center; margin-bottom: 50px; display: flex; flex-direction: column; align-items: center; position: relative; z-index: 1; }
.contact-header h2 { color: #fff; font-size: 2.2rem; }
.contact-header p { color: #94a3b8; font-weight: 600; margin-top: 10px; }

.contact-cards { display: grid; grid-template-columns: repeat(auto-fit, minmax(220px, 1fr)); gap: 20px; position: relative; z-index: 1; }
.c-card {
  background: rgba(255, 255, 255, 0.04); padding: 24px; border-radius: 16px;
  display: flex; align-items: center; gap: 16px;
  border: 1px solid rgba(255, 255, 255, 0.06);
  transition: 0.3s;
}
.c-card:hover {
  transform: translateY(-5px); background: rgba(255, 255, 255, 0.08);
  border-color: rgba(0, 168, 232, 0.3);
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.3);
}
.c-icon {
  width: 48px; height: 48px; background: rgba(0, 168, 232, 0.1); border-radius: 50%;
  display: flex; align-items: center; justify-content: center; color: #00A8E8; font-size: 1.2rem;
  flex-shrink: 0; transition: 0.3s;
}
.c-card:hover .c-icon { background: rgba(255, 214, 0, 0.15); color: #FFD600; }
.instagram .c-icon { color: #E1306C; background: rgba(225, 48, 108, 0.1); }

.c-info { display: flex; flex-direction: column; }
.c-info small { font-size: 0.75rem; text-transform: uppercase; color: #64748b; font-weight: 700; }
.c-info strong { font-size: 1rem; color: #fff; }

/* --- FOOTER --- */
.site-footer {
  background: #000; padding: 40px 0; border-top: 1px solid rgba(255,255,255,0.1);
}
.footer-content {
  max-width: 1200px; margin: 0 auto; padding: 0 24px;
  display: flex; flex-direction: column; align-items: center; gap: 20px;
}
.footer-brand { display: flex; align-items: center; gap: 10px; opacity: 0.8; }
.footer-brand img { height: 30px; }
.footer-brand span { color: #fff; font-weight: 700; font-size: 1.2rem; }
.copyright { color: #64748b; font-size: 0.9rem; text-align: center; }

.footer-logo {
  height: 50px !important;
}

/* --- RESPONSIVE --- */
@media (max-width: 992px) {
  .hero-title { font-size: 2.5rem; }
  .why-us-grid { grid-template-columns: 1fr; }
  .contact-box { padding: 40px 20px; }
  .desktop-links { display: none; }
  .mobile-toggle { display: block; }
}

@media (max-width: 768px) {
  .hero-title { font-size: 2rem; }
  .services-grid { grid-template-columns: 1fr; }
  .contact-cards { grid-template-columns: 1fr; }
}
</style>